import { useEffect } from 'react';
import type { JSX, ReactNode } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { AppChrome } from './components/AppChrome';
import { RequireAuth, useAuth } from './lib/auth';
import {
  browserEventFilterStorage,
  standingUserId,
  touchEventFilterExpiry,
} from './lib/eventFilterMemory';
import { ScanQrProvider } from './lib/scan-qr';
import { AssetDetailPage } from './routes/AssetDetailPage';
import { AssetsPage } from './routes/AssetsPage';
import { AuditPage } from './routes/AuditPage';
import { SettingsPage } from './routes/SettingsPage';
import { BinsPage } from './routes/BinsPage';
import { CatalogPage } from './routes/CatalogPage';
import { DiagnosticsPage } from './routes/DiagnosticsPage';
import { EventDetailPage } from './routes/EventDetailPage';
import { EventsPage } from './routes/EventsPage';
import { HelpPage } from './routes/HelpPage';
import { HomePage } from './routes/HomePage';
import { InventorySessionPage } from './routes/InventorySessionPage';
import { InventoryStartPage } from './routes/InventoryStartPage';
import { LabelsPage } from './routes/LabelsPage';
import { LoginPage } from './routes/LoginPage';
import { NotFoundPage } from './routes/NotFoundPage';
import { QrLandingPage } from './routes/QrLandingPage';
import { ToteDetailPage } from './routes/ToteDetailPage';
import { TotesPage } from './routes/TotesPage';
import { TrailerDetailPage } from './routes/TrailerDetailPage';
import { TrailersPage } from './routes/TrailersPage';
import { ShipmentDetailPage } from './routes/ShipmentDetailPage';
import { ReplacementDetailPage } from './routes/ReplacementDetailPage';
import { ReplacementNewPage } from './routes/ReplacementNewPage';
import { ReplacementsPage } from './routes/ReplacementsPage';
import { UsersPage } from './routes/UsersPage';
import { AvailabilityPage } from './routes/AvailabilityPage';

const PLANNING =
  /^\/(events|catalog|assets|totes|bins|trailers|shipments|admin|availability)(\/|$)/;

function Gate({ children }: { children: ReactNode }): JSX.Element {
  return <RequireAuth>{children}</RequireAuth>;
}

export function App(): JSX.Element {
  const location = useLocation();
  const planning = PLANNING.test(location.pathname);

  return (
    <ScanQrProvider>
      <AppShell planning={planning} />
    </ScanQrProvider>
  );
}

/** A signed-in shell load extends filter expiry. An idle tab does not re-run this. */
function EventFilterVisit(): null {
  const { user, code, loading } = useAuth();
  const userId = standingUserId(user?.id, code !== null);

  useEffect(() => {
    if (loading || !userId) return;
    const storage = browserEventFilterStorage();
    if (!storage) return;
    touchEventFilterExpiry(storage, userId, Date.now());
  }, [loading, userId]);

  return null;
}

function AppShell({ planning }: { planning: boolean }): JSX.Element {
  return (
    <div className={planning ? 'app app--planning' : 'app'}>
      <EventFilterVisit />
      <AppChrome />

      <main className="app__main">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/diagnostics" element={<DiagnosticsPage />} />
          <Route path="/help" element={<HelpPage />} />
          <Route path="/help/:slug" element={<HelpPage />} />
          <Route
            path="/admin/users"
            element={
              <Gate>
                <UsersPage />
              </Gate>
            }
          />
          <Route
            path="/admin/labels"
            element={
              <Gate>
                <LabelsPage />
              </Gate>
            }
          />
          <Route
            path="/admin/audit"
            element={
              <Gate>
                <AuditPage />
              </Gate>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <Gate>
                <SettingsPage />
              </Gate>
            }
          />
          <Route
            path="/events"
            element={
              <Gate>
                <EventsPage />
              </Gate>
            }
          />
          <Route
            path="/events/:id"
            element={
              <Gate>
                <EventDetailPage />
              </Gate>
            }
          />
          <Route
            path="/availability"
            element={
              <Gate>
                <AvailabilityPage />
              </Gate>
            }
          />
          <Route
            path="/availability/:userId"
            element={
              <Gate>
                <AvailabilityPage />
              </Gate>
            }
          />
          <Route
            path="/catalog"
            element={
              <Gate>
                <CatalogPage />
              </Gate>
            }
          />
          <Route
            path="/assets"
            element={
              <Gate>
                <AssetsPage />
              </Gate>
            }
          />
          <Route
            path="/assets/:serial"
            element={
              <Gate>
                <AssetDetailPage />
              </Gate>
            }
          />
          <Route
            path="/totes"
            element={
              <Gate>
                <TotesPage />
              </Gate>
            }
          />
          <Route
            path="/totes/:number"
            element={
              <Gate>
                <ToteDetailPage />
              </Gate>
            }
          />
          <Route
            path="/bins"
            element={
              <Gate>
                <BinsPage />
              </Gate>
            }
          />
          <Route
            path="/trailers"
            element={
              <Gate>
                <TrailersPage />
              </Gate>
            }
          />
          <Route
            path="/trailers/:id"
            element={
              <Gate>
                <TrailerDetailPage />
              </Gate>
            }
          />
          <Route
            path="/shipments/:id"
            element={
              <Gate>
                <ShipmentDetailPage />
              </Gate>
            }
          />
          <Route
            path="/replacements"
            element={
              <Gate>
                <ReplacementsPage />
              </Gate>
            }
          />
          <Route
            path="/replacements/new"
            element={
              <Gate>
                <ReplacementNewPage />
              </Gate>
            }
          />
          <Route
            path="/replacements/:id"
            element={
              <Gate>
                <ReplacementDetailPage />
              </Gate>
            }
          />
          <Route
            path="/inventory/new"
            element={
              <Gate>
                <InventoryStartPage />
              </Gate>
            }
          />
          <Route
            path="/inventory/:sessionId"
            element={
              <Gate>
                <InventorySessionPage />
              </Gate>
            }
          />
          <Route
            path="/t/:number"
            element={
              <Gate>
                <ToteDetailPage qrLanding />
              </Gate>
            }
          />
          <Route
            path="/a/:serial"
            element={
              <Gate>
                <AssetDetailPage qrLanding />
              </Gate>
            }
          />
          <Route
            path="/q/:slug"
            element={
              <Gate>
                <QrLandingPage />
              </Gate>
            }
          />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>

      <footer className="app__footer" data-testid="legal-footer">
        Elite Wrestling Productions
      </footer>
    </div>
  );
}
